import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Card,
  CardContent,
  ToggleButton,
  Tooltip,
  ToggleButtonGroup,
  MenuItem,
  Alert,
  TextField,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import { ResponsiveContainer } from 'recharts';
import { isPending } from '../../services/apiUtils.js';
import WarningSnackbar from '../../components/WarningSnackbar.js';
import { ThemeProvider, styled, alpha } from '@mui/material/styles';
import ResearchTheme from '../../styles/Theme.js';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { FREE_RESEARCH_LIMITS, SUBSCRIPTION_LIMITS } from '../../payment/PricingUtils';
import { useAuth } from '../../authentication/AuthContext';
import PersonaBarChart from '../../components/PersonaBarChart.js';
import CsvUploader from './CsvUploader';
import AiPersonaGenerator from './AIPersonaGenerator';
import SurveyContextEditor from './SurveyContextEditor';

import { 
  formatAttributeLabel,
  FILTER_CONFIG,
} from './filterConfig';

/* ------------------------------------------------------------------
   Wide full-width rectangular toggle group
------------------------------------------------------------------ */
const WideToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  '& .MuiToggleButton-root': {
    flex: 1,                    // both buttons share width equally
    borderColor: theme.palette.divider,
    borderRadius: 0,
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1.2, 0),
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08), // Default hover color
    },
  },
  '& .Mui-selected': {
    backgroundColor: theme.palette.primary.main, // Use primary color from theme
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark, // Darker shade for hover
    },
  },
}));


function Step2Personas({ userData, setUserData, onTopicUpdate, setTabIndex, progress, setProgress, readOnly }) {
  const [importTabActive, setImportTabActive] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAttribute, setSelectedAttribute] = useState(''); 
  const [currentPage, setCurrentPage] = useState(1);
  const [groupBy, setGroupBy] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [openWarningSnackbar, setOpenWarningSnackbar] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(true);
  
  const { isAdmin, hasActiveSubscription } = useAuth();

  const getMaxPersonas = () => {
    if (isAdmin) {
      return 200; // Admin limit
    } else if (hasActiveSubscription) {
      return SUBSCRIPTION_LIMITS.MAX_PERSONAS; // 100 for subscription users
    } else {
      return FREE_RESEARCH_LIMITS.MAX_PERSONAS; // 20 for free users
    }
  };

  const maxPersonas = getMaxPersonas();

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (userData.importedPersonas) {
      setImportTabActive(true);
    } else {
      setImportTabActive(false);
    }
  }, [userData.importedPersonas]);

  const data = useMemo(() => {
    if (!userData?.selectedPersonas || Object.keys(userData.selectedPersonas).length === 0) {
      return {};
    }

    // Get all unique attributes from personas
    const allAttributes = new Set();
    Object.values(userData.selectedPersonas).forEach(persona => {
      Object.keys(persona).forEach(key => {
        if (key !== 'name') allAttributes.add(key);
      });
    });

    // Define the desired order for attributes based on FILTER_CONFIG
    const desiredOrder = [
      'gender',
      'age_range',
      'employment_status',
      'income',
      'education',
      'household_situation',
      'marital_status'
    ];

    // Sort attributes according to desired order
    const sortedAttributes = [];
    const remainingAttributes = [];
    
    desiredOrder.forEach(attr => {
      if (allAttributes.has(attr)) {
        sortedAttributes.push(attr);
      }
    });
    
    Array.from(allAttributes).forEach(attr => {
      if (!desiredOrder.includes(attr)) {
        remainingAttributes.push(attr);
      }
    });
    
    const finalAttributeOrder = [...sortedAttributes, ...remainingAttributes];

    const calculatePercentages = (attribute) => {
      const totalCount = Object.values(userData.selectedPersonas).length;
      const counts = {};

      Object.values(userData.selectedPersonas).forEach(persona => {
        if (!persona[attribute]) return;
        const key = persona[attribute];
        counts[key] = (counts[key] || 0) + 1;
      });

      let result = Object.entries(counts).map(([key, count]) => ({
        name: key,
        value: +(count / totalCount * 100).toFixed(1),
        count
      }));

      // Sort results according to filterConfig options order
      const filterConfig = Object.values(FILTER_CONFIG).find(config => config.apiKey === attribute);
      if (filterConfig && filterConfig.options) {
        result.sort((a, b) => {
          const indexA = filterConfig.options.indexOf(a.name);
          const indexB = filterConfig.options.indexOf(b.name);
          // If an item is not in the options array, put it at the end
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      }

      return result;
    };

    const result = {};
    // Calculate percentages for attributes in the desired order
    finalAttributeOrder.forEach(attr => {
      result[attr] = calculatePercentages(attr);
    });
    
    return result;
  }, [userData?.selectedPersonas]);

  // Add this effect to set the first attribute as default when data changes
  useEffect(() => {
    if (Object.keys(data).length > 0 && !selectedAttribute) {
      setSelectedAttribute(Object.keys(data)[0]);
    }
  }, [data, selectedAttribute]);

  const selectedPersonas = userData?.selectedPersonas || {};
  const hasPersonas = selectedPersonas && Object.keys(selectedPersonas).length > 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleContinue = async () => {
    if (readOnly) return;
    setTabIndex(2);
    if (progress < 3) {
      setProgress(3);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const personasPerPage = 10;

  const filteredPersonas = Object.entries(selectedPersonas).filter(([key, persona]) => {
    const personaValues = Object.values(persona).map(value => value.toLowerCase());
    return personaValues.some(value => value.includes(searchQuery.toLowerCase()));
  });

  const indexOfLastPersona = currentPage * personasPerPage;
  const indexOfFirstPersona = indexOfLastPersona - personasPerPage;
  const currentPersonas = filteredPersonas.slice(indexOfFirstPersona, indexOfLastPersona);

  const prepareStackedData = (attribute, groupBy) => {
    if (!selectedPersonas || Object.keys(selectedPersonas).length === 0) {
      return [];
    }

    if (!groupBy) return data[attribute];

    const groupedData = {};

    Object.values(selectedPersonas).forEach(persona => {
      const attrValue = persona[attribute];
      const groupValue = persona[groupBy];

      if (!groupedData[attrValue]) {
        groupedData[attrValue] = { name: attrValue };
      }

      groupedData[attrValue][groupValue] = (groupedData[attrValue][groupValue] || 0) + 1;
    });


    // return result;
    return Object.values(groupedData);
  };

  return (
    <ThemeProvider theme={ResearchTheme}>
      {!readOnly && (
        <Accordion 
          expanded={isAccordionExpanded}
          onChange={(e, expanded) => setIsAccordionExpanded(expanded)}
      sx={{ mb: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5">
            {'Select Personas'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              These personas will represent your target audience and answer your survey.
            </Typography>

              {/* Alert for user's plan limits */}
            {isAdmin ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                With beta access you can use up to {maxPersonas} personas per research.
              </Alert>
            ) : hasActiveSubscription ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Your subscription allows up to {maxPersonas} personas per research.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
              Free research is limited to {maxPersonas} personas maximum. Join our beta program to access up to {SUBSCRIPTION_LIMITS.MAX_PERSONAS} personas. <a href="/become-beta" style={{ color: 'var(--tertiary-color)', textDecoration: 'underline' }}>Apply for beta access here</a>.
              </Alert>
            )}

              {/* Generator / Importer */}
            <Paper sx={{ p: 3, mt: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Generate or Import Personas
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                {/* Full-width rectangular toggle */}
                <WideToggleButtonGroup
                  value={importTabActive}
                  exclusive
                  onChange={(e, val) => val !== null && setImportTabActive(val)}
                >
                  <ToggleButton value={false}>AI Generated</ToggleButton>
                  <ToggleButton value={true}>Import Your Own</ToggleButton>
                </WideToggleButtonGroup>
                
                {!importTabActive && (
                  <AiPersonaGenerator
                    userData={userData}
                    setUserData={setUserData}
                    onTopicUpdate={onTopicUpdate}
                    setError={setError}
                    setWarningMessage={setWarningMessage}
                    setOpenWarningSnackbar={setOpenWarningSnackbar}
                    maxPersonas={maxPersonas}
                    isAdmin={isAdmin}
                    hasActiveSubscription={hasActiveSubscription}
                    setCurrentPage={setCurrentPage}
                    setSelectedAttribute={setSelectedAttribute}
                  />
                )}
                
                {importTabActive && (
                  <CsvUploader
                    setUserData={setUserData}
                    userData={userData}
                    onTopicUpdate={onTopicUpdate}
                    setError={setError}
                    setWarningMessage={setWarningMessage}
                    setOpenWarningSnackbar={setOpenWarningSnackbar}
                    maxPersonas={maxPersonas}
                    isAdmin={isAdmin}
                    hasActiveSubscription={hasActiveSubscription}
                    setCurrentPage={setCurrentPage}
                    setSelectedAttribute={setSelectedAttribute}
                  />
                )}
              </Box>
            </Paper>

            <SurveyContextEditor 
              userData={userData} 
              setUserData={setUserData} 
              setWarningMessage={setWarningMessage} 
              setOpenWarningSnackbar={setOpenWarningSnackbar} 
              onTopicUpdate={onTopicUpdate} 
              readOnly={readOnly}
            />

            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                Error: {error}
              </Typography>
            )}

            <Tooltip title="Please select personas before continuing">
              <Box 
                sx={{
                  width: 150,
                  height: 70,
                  borderRadius: 1,
                  float: 'right'
                }}>
                {hasPersonas ? (
                  <Button
                    variant="outlined"
                    onClick={handleContinue}
                    sx={{ mt: 2, float: 'right' }}
                  >
                    Continue →
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    disabled={true}
                    sx={{ mt: 2, float: 'right', pointerEvents: 'none' }}
                  >
                    Continue →
                  </Button>
                )}
              </Box>
            </Tooltip>
          </Box>
        </AccordionDetails>
        </Accordion>
      )}

      {hasPersonas && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h5" gutterBottom>
            Your Personas
          </Typography>
          <ToggleButtonGroup
            value={selectedAttribute}
            exclusive
            onChange={(e, newValue) => newValue && setSelectedAttribute(newValue)}
            sx={{
                mb: 4,
                flexWrap: 'wrap',
                width: '100%',
                '& .MuiToggleButton-root': {
                  border: '1px solid', 
                  borderColor: 'divider',
                },
              }}
              >
              {Object.keys(data).map(attr => (
                <ToggleButton key={attr} value={attr}>
                  {formatAttributeLabel(attr)}
                </ToggleButton>
              ))}
          </ToggleButtonGroup>

          <ResponsiveContainer width='100%' height={500}>
            <PersonaBarChart
              data={data}
              selectedAttribute={selectedAttribute}
              groupBy={groupBy}
              prepareStackedData={prepareStackedData}
              formatAttributeLabel={formatAttributeLabel}
            />
          </ResponsiveContainer>

          <Accordion sx={{ mt: 3, borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">Grouped Analysis</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Select a grouping attribute to analyze personas in more detail.
                </Typography>

                <TextField
                  select
                  label="Group by"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">None</MenuItem>
                  {Object.keys(data).map(attr => (
                    <MenuItem key={attr} value={attr}>
                      {formatAttributeLabel(attr)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>
      )}

      {hasPersonas && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Pagination
              count={Math.ceil(filteredPersonas.length / personasPerPage)}
              page={currentPage}
              onChange={handlePageChange}
              sx={{ mt: 2 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, mt: 1 }}>
              <TextField
                label="Search"
                variant="outlined"
                size="small" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: '200px', mr: 2, mb: 2 }} 
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}>
          {currentPersonas.length > 0 ? (
                currentPersonas.map(([key, persona]) => (
                  <Grid item xs={12} sm={6} key={key} sx={{ display: 'flex' }}>
                    <Card sx={{ mb: 2, flexGrow: 1 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{mb: 2}}>
                          {persona.name || `Persona ${key}`}
                        </Typography>
                        {Object.entries(persona).map(([attrKey, attrValue]) => {
                          if (attrKey === 'name' || !attrValue) return null;
                          return (
                            <Box key={attrKey} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Typography sx={{ fontWeight: 'bold', ml: 1 }}>
                                {formatAttributeLabel(attrKey)}:
                              </Typography>
                              <Typography sx={{ ml: 1 }}>
                                {attrValue}
                              </Typography>
                            </Box>
                          );
                        })}
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Typography variant="body1" sx={{ mt: 2, mb: 4, width: '100%', textAlign: 'center' }}>
                  No personas match your search.
                </Typography>
              )}
          </Grid>
        </Paper>
      )}

      {!readOnly && (
      <Tooltip title="Please select personas before continuing">
        <Box 
          sx={{
            width: 150,
            height: 50,
            borderRadius: 1,
            float: 'center',
            margin: 'auto',              
          }}>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={!hasPersonas}
            sx={{ mt: 2, display: 'block', mx: 'auto'}}
          >
            Continue →
          </Button>

          <WarningSnackbar
            open={openWarningSnackbar}
            message={warningMessage}
            onClose={() => setOpenWarningSnackbar(false)}
          />
        </Box>
      </Tooltip>
      )}
    </ThemeProvider>
  );
}

export default Step2Personas;
